import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const [userResponse, permResponse] = await Promise.all([
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/auth/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setUser(userResponse.data);
      setPermissions(permResponse.data.permissions || {});
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Fetch permissions after login
    try {
      const permResponse = await axios.get(`${API}/auth/permissions`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setPermissions(permResponse.data.permissions || {});
    } catch (e) {
      console.error('Failed to fetch permissions:', e);
    }
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setPermissions({});
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    return permissions[permission] === true;
  };

  // Check if user is admin
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      permissions,
      loading, 
      login, 
      logout, 
      getAuthHeaders,
      hasPermission,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};
